import { useState } from 'react';
import { ExternalLink, ImageOff, Maximize2, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { safeHttpsUrl } from '../../utils/url';
import { buildSpotUrl } from '../../utils/spotLink';
import { colorForRetailer } from '../production/chartTheme';
import { formatSpotDate } from './SpotHistoryGrid';

/**
 * The two Tableau click actions for a spot: see its image, open it in SW1.
 *
 * Rendered as a side panel inside the modal (bottom sheet on narrow screens).
 * The parent keys it by spotId so the image error state resets per spot.
 */
export default function SpotDetailPanel({ spot, productsInSpot, onClose, onOpenImage }) {
  const [imageFailed, setImageFailed] = useState(false);
  const imageUrl = safeHttpsUrl(spot.spotImage);
  const spotUrl = buildSpotUrl(spot.spotId);
  const showImage = imageUrl && !imageFailed;

  return (
    <aside
      aria-label={`Spot ${spot.spotId}`}
      className={cn(
        'bg-white border border-neutral-200 rounded-lg shadow-sm flex flex-col min-h-0',
        'shrink-0 lg:w-80 max-h-[45vh] lg:max-h-none'
      )}
    >
      <div className="flex items-start justify-between gap-3 px-4 pt-3 pb-2 border-b border-neutral-100">
        <div>
          <h4 className="text-sm font-semibold text-neutral-900 flex items-center gap-1.5">
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: colorForRetailer(spot.customername) }}
            />
            {spot.customername} · {formatSpotDate(spot.startdate)}
          </h4>
          <p className="text-xs text-neutral-500 tabular-nums mt-0.5">Spot {spot.spotId}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-md text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100"
          aria-label="Close spot detail"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="overflow-y-auto px-4 py-3 space-y-3">
        {showImage ? (
          <button
            type="button"
            onClick={() => onOpenImage(imageUrl)}
            className="group relative block w-full rounded-md overflow-hidden border border-neutral-200 bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            title="View full size"
          >
            <img
              src={imageUrl}
              alt={`Spot ${spot.spotId} image`}
              className="w-full max-h-64 object-contain"
              onError={() => setImageFailed(true)}
            />
            <span className="absolute top-1.5 right-1.5 p-1 rounded bg-white/90 text-neutral-600 opacity-0 group-hover:opacity-100 transition-opacity">
              <Maximize2 className="w-3.5 h-3.5" />
            </span>
          </button>
        ) : (
          <div className="flex flex-col items-center justify-center gap-1.5 h-32 rounded-md border border-dashed border-neutral-300 text-neutral-400 text-xs">
            <ImageOff className="w-5 h-5" />
            Image not available
          </div>
        )}

        <dl className="grid grid-cols-2 gap-2 text-xs">
          <Field label="Page" value={spot.pagenumber ?? '—'} />
          <Field label="Products" value={productsInSpot} />
          <Field label="Promo type" value={spot.promotype || '—'} />
          <Field label="Ad type" value={spot.adtype || '—'} />
        </dl>

        {spotUrl && (
          <a
            href={spotUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'w-full inline-flex items-center justify-center gap-1.5 font-medium rounded-md',
              'px-3 py-2 text-sm bg-primary-600 text-white hover:bg-primary-700',
              'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
            )}
          >
            Open spot in SW1
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
    </aside>
  );
}

function Field({ label, value }) {
  return (
    <div className="bg-neutral-50 rounded p-2">
      <dt className="text-[9px] uppercase tracking-wider text-neutral-500 font-semibold">{label}</dt>
      <dd className="text-sm font-semibold text-neutral-900 mt-0.5 truncate">{value}</dd>
    </div>
  );
}
